import { Divider } from '@startuptools/ui/Divider';
import { Stack } from '@startuptools/ui/Stack';
import { Document } from '@startuptools/business-ui/document/Document';
import { TagsInput } from '../../../../components/document-dialog/DocumentDialog';
import { DocumentSignatures } from '../../../../components/document-signatures/DocumentSignatures';
import {
  type GqlSipShareSubscriptionNoteListQuery,
  SipShareSubscriptionNoteListDocument,
} from './SipShareSubscriptionNoteList.graphql';
import { type ArrayElement } from '@startuptools/common/common';
import {
  FileDocument,
  useActiveCompanyStripeSubLazyQuery,
  useDocGenShareSubscriptionListMutation,
  useMeetingPaidForConcernsLazyQuery,
  useShareIssueErrorsLazyQuery,
  useSipValidatorErrorsLazyQuery,
  useSirErrorsLazyQuery,
} from '../../../../graphql/react-operations';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { isNil, omit } from 'lodash-es';
import { useState } from 'react';
import {
  type GqlCompany,
  GqlDocumentSignatureMethod,
  type GqlMeetingConcern,
  type GqlShareIssue,
  type GqlValidatorStatus,
} from '../../../../graphql/base-types.graphql';
import { apiClient } from '../../../../common/http';
import { InnerStripe } from '../../../../components/stripe/Stripe';
import { checkNeedsPayment, hasValidatorErrors } from '../../utils';
import { ValidatorErrorDialog } from '../ValidatorErrorDialog';
import { useNavigate } from '../../../../components/Navigate';

export const SipShareSubscriptionNote = ({
  shareIssue,
  sip,
  company,
}: {
  shareIssue: Pick<GqlShareIssue, 'id' | 'description' | 'paidForAt' | 'companyId'> & {
    meetingConcern?: Pick<GqlMeetingConcern, 'meetingId'> | null;
  };
  sip: ArrayElement<ArrayElement<GqlSipShareSubscriptionNoteListQuery['shareIssueRegistrations']>['participants']>;
  company: Pick<GqlCompany, 'name'>;
}) => {
  const companyId = useCompanyId();
  const [showStripeDialog, setShowStripeDialog] = useState<{
    open: boolean;
    signatureMethod?: GqlDocumentSignatureMethod;
  }>({ open: false });
  const [validatorErrors, setValidatorErrors] = useState<GqlValidatorStatus[] | null>(null);
  const { navigate } = useNavigate();

  const [fetchPaidFor] = useMeetingPaidForConcernsLazyQuery({ fetchPolicy: 'no-cache' });
  const [doGetActiveSub] = useActiveCompanyStripeSubLazyQuery({ variables: { companyId }, fetchPolicy: 'no-cache' });

  // eslint-disable-next-line sut/loading-mutation
  const [genDoc] = useDocGenShareSubscriptionListMutation({
    refetchQueries: [SipShareSubscriptionNoteListDocument, FileDocument],
  });
  const [fetchSiErrors] = useShareIssueErrorsLazyQuery({
    variables: { companyId, id: shareIssue.id },
    fetchPolicy: 'no-cache',
  });
  const [fetchSirErrors] = useSirErrorsLazyQuery({
    variables: { companyId, id: sip.shareIssueRegistrationId },
    fetchPolicy: 'no-cache',
  });
  const [fetchSipErrors] = useSipValidatorErrorsLazyQuery({
    variables: { companyId, id: sip.id },
    fetchPolicy: 'no-cache',
  });

  const generateDoc = async (signatureMethod: GqlDocumentSignatureMethod) => {
    if (shareIssue.paidForAt) {
      await genDoc({
        variables: { companyId: shareIssue.companyId, sipId: sip.id, signatureMethod },
      });
      return;
    }
    const id = shareIssue.meetingConcern?.meetingId;
    if (!id) {
      throw new Error('Unexpected state: no meetingId');
    }
    const { data } = await fetchPaidFor({ variables: { companyId, id } });
    const paidFor = data?.meetingPaidForConcerns;
    if (!paidFor) {
      throw new Error('Unexpected state: no paidFor');
    }
    const { data: activeSubData } = await doGetActiveSub();
    if (!activeSubData) {
      throw new Error('Could not get active subscription');
    }
    const allMcs = [...paidFor.shareIssues, ...paidFor.warrantPrograms];
    const needsPayment = checkNeedsPayment(activeSubData.activeCompanyStripeSubscription, allMcs);
    if (needsPayment) {
      setShowStripeDialog({ open: true, signatureMethod });
      return;
    }
    await genDoc({
      variables: { companyId: shareIssue.companyId, sipId: sip.id, signatureMethod },
    });
  };

  const defaultFilename = `Teckningsanmälan nyemission - ${sip.identity.name} - ${company.name} - ${shareIssue.description}.pdf`;

  return (
    <>
      <Document
        tagsSlot={
          sip.latestShareSubscriptionList && (
            <Stack direction="row" gap={2}>
              <TagsInput document={sip.latestShareSubscriptionList} />
            </Stack>
          )
        }
        signaturesSlot={
          sip.latestShareSubscriptionList?.signatureMethod === GqlDocumentSignatureMethod.Digital && (
            <>
              <Divider />
              <DocumentSignatures
                documentId={sip.latestShareSubscriptionList.id}
                scriveStatus={sip.latestShareSubscriptionList.scriveStatus}
                zignedAgreementStatus={sip.latestShareSubscriptionList.zignedAgreementStatus}
                signAllowedAction={sip.latestShareSubscriptionList.signAllowedAction}
              />
            </>
          )
        }
        document={sip.latestShareSubscriptionList}
        defaultFileName={defaultFilename}
        generate={generateDoc}
        preview={() => {
          return apiClient.previewShareSubscriptionList(companyId, sip.id);
        }}
        validate={async () => {
          const { data: siErrorsData } = await fetchSiErrors();
          const { data: sirErrorData } = await fetchSirErrors();
          const { data: sipErrorData } = await fetchSipErrors();
          if (isNil(siErrorsData) || isNil(sirErrorData) || isNil(sipErrorData)) {
            throw new Error('unexpected missing siError, sirError or sipError');
          }
          const siErrors = siErrorsData.shareIssueErrors;
          const sirErrors = omit(
            sirErrorData?.shareIssueRegistrationErrors,
            'paymentStatus',
            'participants',
            'signsRegForm',
            'decision',
            'todos',
          );
          const sipErrors = omit(sipErrorData.sipValidatorErrors, 'paymentStatus');
          if (hasValidatorErrors(siErrors) || hasValidatorErrors(sirErrors) || hasValidatorErrors(sipErrors)) {
            return {
              isValid: false,
              errors: [
                ...Object.values(siErrors),
                ...Object.values(sirErrors),
                ...Object.values(sipErrors),
              ] as GqlValidatorStatus[],
            };
          }
          return { isValid: true, errors: undefined };
        }}
      />
      {showStripeDialog.open && shareIssue.meetingConcern?.meetingId && (
        <InnerStripe
          companyId={companyId}
          meetingId={shareIssue.meetingConcern?.meetingId}
          onCancel={() => {
            setShowStripeDialog({ open: false });
          }}
          onPaid={() => {
            setShowStripeDialog({ open: false });
            return genDoc({
              variables: {
                companyId,
                sipId: sip.id,
                signatureMethod: showStripeDialog.signatureMethod ?? GqlDocumentSignatureMethod.Analogue,
              },
            });
          }}
          onBillingRedirect={() => navigate('/settings/billing')}
        />
      )}
      {validatorErrors && <ValidatorErrorDialog errors={validatorErrors} onClose={() => setValidatorErrors(null)} />}
    </>
  );
};
